import React, { useContext, useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Item from "./Item";
import Background from "./Background";
import { ComponentSkeleton } from "./SkeletonPlaceholder";
import useSpacing from '../../core/hooks/useSpacing';
import useBaseStyle from '../../core/hooks/useBaseStyle';
import useImageStyle from '../../core/hooks/useImageStyle';
import useContainerStyle from '../../core/hooks/useContainerStyle';
import { getContainerInsetShadowClass } from '../../core/utils/containerStyleUtils';
import EditableElement from "../../editor/components/EditableElement";
import { DragHandleProvider } from "../../editor/context/DragHandleContext";
import "../patterns.css";
import { getBackgroundPatternClass } from '../../core/utils/backgroundPatternUtils';
import AddButtonWithIndicator from "../../editor/components/AddButtonWithIndicator";
import ContainerEffectOverlay from "./ContainerEffectOverlay";
import ComponentAutomatic from "./ComponentAutomatic";
import AddButtonOverlay from "../../editor/add-buttons/AddButtonOverlay";
import { useAddElement } from "../../editor/context/AddElementContext";
import { EditContext } from "../../editor/context/EditContext";
import { usePreviewOptional } from "../../editor/context/PreviewContext";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { makeSortableId } from "../../editor/utils/reorderCache";
import { apiFetch } from "../../core/utils/api";
import { getBreakpoint } from '../../core/utils/pageUtils';
import { Z_INDEX } from "../../core/utils/zIndexScale";

function sortChildElements(elements = []) {
  return (elements || [])
    .filter(Boolean)
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
}

function groupChildElements(elements, layoutList) {
  if (!Array.isArray(layoutList) || layoutList.length === 0) {
    return elements.map((e) => [e]);
  }
  const rows = [];
  let i = 0;
  for (const count of layoutList) {
    if (i >= elements.length) break;
    if (count > 0) {
      rows.push(elements.slice(i, i + count));
      i += count;
    }
  }
  if (i < elements.length) {
    rows.push(elements.slice(i));
  }
  return rows;
}

const DEFAULT_MIN_WIDTH = "250px";
const ROW_STYLE = { width: "100%" };
const getCellStyle = (count) => ({
  width: `${100 / count}%`,
  minWidth: DEFAULT_MIN_WIDTH,
});

/**
 * Detects if child data is fully serialized (preview mode) or minimal ref (lazy loading).
 * 
 * See Hero.js for full documentation. This helper enables components to work with
 * both template previews (full data) and normal website viewing (lazy loading).
 * 
 * @param {Object} child - Child element data (item or component)
 * @returns {boolean} True if child has full data, false if minimal ref
 */
const hasFullData = (child) => {
  if (!child) return false;
  const minimalFields = ['id', 'type', 'order'];
  const allFields = Object.keys(child);
  return allFields.some(field => !minimalFields.includes(field));
};

// Placeholder to highlight unexpected elements (missing or unknown type)
const UnknownElement = ({ element }) => (
  <div className="p-4 bg-red-100 text-red-600 border border-red-300 rounded">
    Unknown element with id {element.id} – missing or unrecognised type
  </div>
);

// Content renderer (components and items)
const SortableItemWrapper = ({ id, data, children }) => {
  // dnd-kit treats `data` as an input to its internal registration effects.
  // Passing a new object each render can cause re-registration churn and, in some cases,
  // "Maximum update depth exceeded" loops. Stabilize by memoizing by value.
  const stableData = useMemo(() => data, [JSON.stringify(data ?? null)]);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data: stableData });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.18 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <DragHandleProvider value={{ dragListeners: listeners, dragAnywhereEnabled: true }}>
        {children}
      </DragHandleProvider>
    </div>
  );
};

const RenderElement = ({ element }) => {
  if (element.type === "component") {
    return <Component {...(hasFullData(element) ? { item: element } : { componentId: element.id })} />;
  }
  if (element.type === "button_collection") {
    return <Item {...(hasFullData(element) ? { item: element } : { itemRef: { id: element.id, type: element.type } })} />;
  }
  if (element.type) {
    return <Item {...(hasFullData(element) ? { item: element } : { itemRef: element })} />;
  }
  return <UnknownElement element={element} />;
};

const getChildKey = (element) => `${element?.type || "unknown"}:${element?.id ?? "missing"}`;

const RenderItems = ({ aggregatedSorted, compId, rows }) => {
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;
  const { openAddModal } = useAddElement();
  const droppableId = `parent:component:${compId}`;
  const { setNodeRef } = useDroppable({ id: droppableId });
  const itemsSignature = Array.isArray(aggregatedSorted)
    ? aggregatedSorted.map((e) => makeSortableId(e)).join("|")
    : "";
  const itemsIds = useMemo(() => {
    if (!Array.isArray(aggregatedSorted)) return [];
    return aggregatedSorted.map((e) => makeSortableId(e));
  }, [itemsSignature]);
  const displayIndexByKey = useMemo(() => {
    const map = new Map();
    (aggregatedSorted || []).forEach((element, index) => {
      map.set(getChildKey(element), index);
    });
    return map;
  }, [itemsSignature, aggregatedSorted]);
  const getSortableData = (element, fallbackIndex) => ({
    parentType: "component",
    parentId: compId,
    kind: element.type === "component" ? "component" : "item",
    itemType: element.type,
    index: displayIndexByKey.get(getChildKey(element)) ?? fallbackIndex,
    autoLayout: false,
    autoBucket: null,
  });

  if (!isEditMode) {
    if (!aggregatedSorted || aggregatedSorted.length === 0) {
      return <div ref={setNodeRef} id={droppableId} className="h-24 w-full" />;
    }

    return (
      <div ref={setNodeRef} id={droppableId} className="aggregated-child-elements">
        {rows ? (
          rows.map((row, ri) => (
            <div
              key={ri}
              className="flex flex-wrap justify-center m-0 p-0"
              style={ROW_STYLE}
            >
              {row.map((element) => (
                <div key={element.id} className="p-0 m-0" style={getCellStyle(row.length)}>
                  <RenderElement element={element} />
                </div>
              ))}
            </div>
          ))
        ) : (
          aggregatedSorted.map((element) =>
            element.type === "component" ? (
              <Component key={element.id} {...(hasFullData(element) ? { item: element } : { componentId: element.id })} />
            ) : element.type === "button_collection" ? (
              <Item key={element.id} {...(hasFullData(element) ? { item: element } : { itemRef: { id: element.id, type: element.type } })} />
            ) : element.type ? (
              <Item key={element.id} {...(hasFullData(element) ? { item: element } : { itemRef: element })} />
            ) : (
              <UnknownElement key={element.id} element={element} />
            )
          )
        )}
      </div>
    );
  }

  if (aggregatedSorted == null || aggregatedSorted.length === 0) {
    return (
      <div ref={setNodeRef} id={droppableId} className="relative min-h-[4rem] w-full group">
        <AddButtonOverlay
          layer="component-empty"
          overlayId={`component-empty-${compId}`}
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
        >
          <AddButtonWithIndicator
            onClick={(e) => {
              e.stopPropagation();
              openAddModal({ componentId: compId, order: 0 }, e);
            }}
          />
        </AddButtonOverlay>
      </div>
    );
  }
  return (
    <SortableContext items={itemsIds} strategy={verticalListSortingStrategy}>
      <div ref={setNodeRef} id={droppableId} className="aggregated-child-elements">
        {rows ? (
          rows.map((row, ri) => (
            <div
              key={ri}
              className="flex flex-wrap justify-center m-0 p-0 relative isolate group hover:z-20 focus-within:z-20"
              style={ROW_STYLE}
            >
              {row.map((element, idx) => (
                <div key={element.id} className="p-0 m-0" style={getCellStyle(row.length)}>
                  <SortableItemWrapper
                    id={makeSortableId(element)}
                    data={getSortableData(element, idx)}
                  >
                    <RenderElement element={element} />
                  </SortableItemWrapper>
                </div>
              ))}
              {isEditMode && ri === 0 && (
                <AddButtonOverlay
                  layer="component-row-before"
                  overlayId={`component-row-before-${compId}-${ri}`}
                  className="absolute top-0 left-0 w-full -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                >
                  <AddButtonWithIndicator onClick={(e) => { e.stopPropagation(); openAddModal({ componentId: compId, order: ri }, e); }} />
                </AddButtonOverlay>
              )}
              {isEditMode && (
                <AddButtonOverlay
                  layer="component-row-after"
                  overlayId={`component-row-after-${compId}-${ri}`}
                  shift={ri === rows.length - 1 ? "left" : "none"}
                  className="absolute bottom-0 left-0 w-full translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                >
                  <AddButtonWithIndicator onClick={(e) => { e.stopPropagation(); openAddModal({ componentId: compId, order: ri + 1 }, e); }} />
                </AddButtonOverlay>
              )}
            </div>
          ))
        ) : (
          aggregatedSorted.map((el, idx) => (
            <div key={el.id} className="relative isolate group hover:z-20 focus-within:z-20">
              <SortableItemWrapper
                id={makeSortableId(el)}
                data={getSortableData(el, idx)}
              >
                <div className="m-0 p-0">
                  <RenderElement element={el} />
                </div>
              </SortableItemWrapper>
              {isEditMode && idx === 0 && (
                <AddButtonOverlay
                  layer="component-linear-before"
                  overlayId={`component-linear-before-${compId}-${el.id}`}
                  className="absolute top-0 left-0 w-full -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                >
                  <AddButtonWithIndicator onClick={(e) => { e.stopPropagation(); openAddModal({ componentId: compId, order: idx }, e); }} />
                </AddButtonOverlay>
              )}
              {isEditMode && (
                <AddButtonOverlay
                  layer="component-linear-after"
                  overlayId={`component-linear-after-${compId}-${el.id}-${idx}`}
                  shift={idx === aggregatedSorted.length - 1 ? "left" : "none"}
                  className="absolute bottom-0 left-0 w-full translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                >
                  <AddButtonWithIndicator onClick={(e) => { e.stopPropagation(); openAddModal({ componentId: compId, order: idx + 1 }, e); }} />
                </AddButtonOverlay>
              )}
            </div>
          ))
        )}
      </div>
    </SortableContext>
  );
};

const Component = ({ componentId, item: providedItem, containerWidth: providedContainerWidth }) => {
  
  // Get containerWidth from: 1) prop, 2) PreviewContext (edit mode), 3) window width (public routes)
  const { containerWidth: contextWidth } = usePreviewOptional() || {};
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  
  useEffect(() => {
    // Only set up window resize listener if we don't have context width or provided width
    if (providedContainerWidth !== undefined || contextWidth !== undefined) return;
    if (typeof window === 'undefined') return;
    
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize(); // Get initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [contextWidth, providedContainerWidth]);
  
  const containerWidth = providedContainerWidth ?? contextWidth ?? windowWidth;
  
  // React Query fetch for component details.
  // IMPORTANT: keep the query enabled even when we have a hydrated `providedItem`.
  // We use `initialData` to avoid an unnecessary fetch on mount, but still allow
  // refetches when the cache is invalidated (e.g., after AI/content changes).
  const hasInitialData = !!providedItem && !!componentId;
  
  const {
    data: component,
    isLoading: loading,
    error,
    isFetching,
  } = useQuery({
    queryKey: ["component", componentId],
    queryFn: async () => {
      const response = await apiFetch(`/api/components/${componentId}`);
      if (!response.ok) {
        throw new Error(`Error fetching component: ${response.status}`);
      }
      return response.json();
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!componentId,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData, // Keep previous data while refetching to prevent skeleton flicker
    initialData: hasInitialData ? providedItem : undefined,
  });

  // Breakpoint is derived from containerWidth; compute it directly to avoid state/effect update loops.
  const breakpoint = useMemo(
    () => (typeof containerWidth === "number" ? getBreakpoint(containerWidth) : "medium"),
    [containerWidth],
  );

  // Use provided item if available (for previews), otherwise use fetched component
  const safeComponent = providedItem || component || {};
  const { combinedClasses } = useSpacing(safeComponent);
  const { styleClasses, inlineStyles } = useBaseStyle(safeComponent);
  const containerStyleClasses = useContainerStyle(safeComponent);
  const insetShadowClass = getContainerInsetShadowClass(safeComponent.container_style);
  const { styleClasses: imageStyleClasses, inlineStyles: imageInlineStyles } =
    useImageStyle(safeComponent.background?.background_image);
  const showImage = safeComponent.background?.show_image;
  const bgImage =
    showImage && safeComponent.background?.background_image?.image
      ? safeComponent.background.background_image.image
      : null;
  const bgPattern = safeComponent.background?.background_pattern;
  const { colorClass = "", colorStyle = {}, jsPatternLayer = null } =
    getBackgroundPatternClass(bgPattern);

  // Sort child elements and process layout
  const sortedElements = useMemo(() => {
    const childItems = safeComponent.all_child_components_and_items || [];
    console.log('[IMAGE_DELETE_FLOW] Component rendering with child items:', {
      componentId: safeComponent.id,
      childCount: childItems.length,
      childIds: childItems.map(c => ({ id: c.id, type: c.type }))
    });
    const elements = sortChildElements(childItems);
    return elements;
  }, [safeComponent.all_child_components_and_items, safeComponent.id]);

  // Parse layout for current breakpoint (same logic as WebsitePage)
  const layoutList = useMemo(() => {
    // New structure: component.layout is a LayoutByLayout object with breakpoint fields
    const raw = safeComponent.layout?.[breakpoint];
    
    // If it's already an array, use it directly
    if (Array.isArray(raw)) {
      return raw.length > 0 ? raw : null;
    }
    
    // Fallback: handle string for backward compatibility during migration
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
      } catch {
        // Legacy comma-separated values
        const parts = raw.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n) && n > 0);
        return parts.length > 0 ? parts : null;
      }
    }
    
    return raw;
  }, [safeComponent.layout, breakpoint]);

  const manualRows = useMemo(
    () =>
      sortedElements.length > 0 && Array.isArray(layoutList)
        ? groupChildElements(sortedElements, layoutList)
        : null,
    [sortedElements, layoutList],
  );
  const isAutomatic = safeComponent.manual_layout === false && !!safeComponent.component_mode;
  const rows = manualRows;
  const displayElements = sortedElements;
  const isHeroOnlyComponent =
    sortedElements.length === 1 && sortedElements[0]?.type === "hero";

  // Use overflow-hidden for components with rounded borders to ensure background/content clips to radius
  // Detect any Tailwind rounded class except 'rounded-none' (e.g., rounded, rounded-md, rounded-lg, rounded-t-lg, rounded-full)
  const hasRoundedBorder = /\brounded(-(?!none)\S+)?\b/.test(containerStyleClasses || "");
  const overflowClass = hasRoundedBorder ? "overflow-hidden" : "overflow-visible";
  
  const containerClasses = [
    "component-box",
    combinedClasses,
    styleClasses,
    "relative",
    "isolate",
    "group",
    containerStyleClasses,
    overflowClass,
  ]
    .filter(Boolean)
    .join(" ");

  // Only show loading if we don't already have any component data to render.
  // When `providedItem` is passed we use it as initialData, so avoid flashing skeletons.
  if (loading && !safeComponent?.id) return <ComponentSkeleton />;
  if (error) return <div>Error: {error.message}</div>;
  
  // Check safeComponent (which includes providedItem), not just component
  if (!safeComponent || !safeComponent.id) return null;

  const content = (
    <div className={containerClasses} style={inlineStyles}>
      <Background
        bgImage={bgImage}
        imageStyleClasses={imageStyleClasses}
        imageInlineStyles={imageInlineStyles}
        colorClass={colorClass}
        colorStyle={colorStyle}
        jsPatternLayer={jsPatternLayer}
        hoverClass={insetShadowClass}
      />
      <ContainerEffectOverlay data={safeComponent} />
      <div className="relative z-10 overflow-visible">
        {isAutomatic ? (
          <ComponentAutomatic
            component={safeComponent}
            containerWidth={containerWidth}
            breakpoint={breakpoint}
          />
        ) : (
          <RenderItems
            aggregatedSorted={displayElements}
            compId={safeComponent.id}
            rows={rows}
          />
        )}
      </div>
    </div>
  );

  return (
    <EditableElement
      element={safeComponent}
      hideTopRightQuickActions={isHeroOnlyComponent}
      suppressHoverBorder={isAutomatic}
    >
      {content}
    </EditableElement>
  );
};

export default React.memo(Component);
